import React, { useCallback } from 'react'
import Questionnaire from './Questionnaire'
import { publicPsqFormData } from './psqFormData'
import Wrapper from './Wrapper'
import PsqConfirmation from './PsqConfirmation'
import { useDispatch, useSelector } from 'react-redux'
import { selectPsqError, selectPsqLoading, selectPsqResults } from 'redux/selectors'
import { Trans, useTranslation } from 'react-i18next'
import UserMessageFullScreen from 'components/screens/UserMessageFullScreen'
import LoadingSpinnerWithContainer from 'components/generic/LoadingSpinnerWithContainer'
import { MessageTypes } from 'constants/message-screens'
import { ReactComponent as RefreshIcon } from 'images/icons/icn_refresh.svg'
import { submitPsqPublic } from 'actions/psq-actions'
import Storage from 'modules/storage'
import withOnlyAllowLoggedOut from 'components/generic/hoc/withOnlyAllowLoggedOut'
import { roundDownWtn } from 'utils'
import { PsqResults } from 'types/global'
import { PersonPSQAnswerInput } from 'types/api/types'
import { SexEnum } from 'types/api/enums'
import { MIN_AGE_FOR_PSQ } from 'constants/profile'

interface Props {
  registrationUrl: string
}

const PsqPublic = ({ registrationUrl }: Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const psqLoading = useSelector(selectPsqLoading)
  const psqResults = useSelector(selectPsqResults)
  const psqError = useSelector(selectPsqError)
  const handleSubmit = useCallback(
    (answers: PersonPSQAnswerInput[]) => {
      const genderMap = {
        '1': SexEnum.M,
        '2': SexEnum.F,
      }
      const answersInput = answers.slice(0, 5)
      const gender = genderMap[answers[5]?.answerId ?? '1']
      const age = +(answers[6]?.answerId ?? MIN_AGE_FOR_PSQ)

      dispatch(
        submitPsqPublic({
          answers: answersInput,
          gender,
          age,
        })
      )
      Storage.savePublicRmg(answersInput)
    },
    [dispatch]
  )

  if (psqError)
    return (
      <UserMessageFullScreen
        messageType={MessageTypes.PSQ_SUBMIT_ERROR}
        backLink={window.location.href}
        backLinkIcon={<RefreshIcon />}
        backLinkText={t('submitErrorInfo', { ns: 'strPsq' })}
      />
    )

  if (psqLoading)
    return (
      <Wrapper>
        <LoadingSpinnerWithContainer containerHeight={600} />
      </Wrapper>
    )

  return (
    <Wrapper>
      {psqResults ? (
        <PsqConfirmation
          results={psqResults}
          text={<ConfirmationText results={psqResults} />}
          cta={{
            text: t('screenCompleted.public.button', { ns: 'strPsq' }),
            href: registrationUrl,
          }}
        />
      ) : (
        <Questionnaire formData={publicPsqFormData} onSubmit={handleSubmit} />
      )}
    </Wrapper>
  )
}

const ConfirmationText = ({ results }: { results: PsqResults }) => {
  const { t } = useTranslation()
  return (
    <>
      <p>
        <Trans ns='strPsq' i18nKey={'screenCompleted.public.info1'}>
          Your answers have been used to generate a <b>World Tennis Number</b> that you’ll be able
          to use until you start competing.
        </Trans>
      </p>
      <p>
        <Trans ns='strPsq' i18nKey={'screenCompleted.public.info2'}>
          Your <b>Singles number</b> has been set as{' '}
          <b>{{ wtnSingles: roundDownWtn(results.wtnSingles as number) }}</b>
          <br />
          Your <b>Doubles number</b> has been set as{' '}
          <b>{{ wtnDoubles: roundDownWtn(results.wtnDoubles as number) }}</b>
        </Trans>
      </p>
      <p>{t('screenCompleted.public.info3', { ns: 'strPsq' })}</p>
      <Trans ns='strPsq' i18nKey={'screenCompleted.public.info4'}>
        <ul>
          <li>Improving your rating.</li>
          <li>Compare your rating to your clubmates and players around the world</li>
          <li>Track changes to your rating</li>
          <li>Follow friends and opponents and view their results</li>
          <li>Access all of your results in one place</li>
        </ul>
      </Trans>
      <p>
        <b>{t('screenCompleted.public.info5', { ns: 'strPsq' })}</b>
      </p>
    </>
  )
}

export default withOnlyAllowLoggedOut(PsqPublic)
